import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
  Loader,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { FollowupServices } from "../../../core/services/FollowupServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  FollowupFields,
  FollowupListParams,
  Permission,
} from "./FollowupDataTypes";
import "./Followups.css";
import FollowupFilters from "./FollowupFilters";
import {
  displayDayDateField,
  isEmptyVal,
  isNotEmpty,
  isWebResolutionSize,
} from "../../../utils/helpers";
import DescriptionCell from "../../components/Elements/DescriptionCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import { displayField } from "../../../utils/helpers";
import useDecryptedParams from "../../../navigation/useDecryptParams";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  followupStatusObj,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import FollowupSingleAction from "./FollowupSingleAction";
import { CustomerServices } from "../../../core/services/CustomerServices";

function FollowupsForCustomer() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<FollowupFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const params: any = useParams();
  let [decryptedParams] = useDecryptedParams(params);
  const [custLoader, setCustLoader] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<any>({});
  const [isWebResolution, setIsWebResolution] = useState(isWebResolutionSize());

  const initSearchFormData = {
    company: "",
    customer: getCustomerId(),
    user: "",
    title: "",
    follow_up_for: "",
    follow_up_status: followupStatusObj.incomplete.value,
    follow_up_date_from: "",
    follow_up_date_to: "",
    follow_up_date: "",
    date_interval_type: "",
    smart_search: "",
  };

  const [formData, setFormData] =
    useState<FollowupListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };
  function getCustomerId() {
    return decryptedParams.id ? decryptedParams.id.toString() : null;
  }
  async function getViewCustomerDetail(id: number) {
    try {
      setCustLoader(true);
      const res = await CustomerServices.viewCustomer(id);
      const respData = get("data.data", res);
      setCustomerInfo({ ...respData });
      setCustLoader(false);
    } catch (error) {
      setCustLoader(false);
    }
  }
  async function getFollowups(params: FollowupListParams) {
    try {
      setLoading(true);
      const res = await FollowupServices.getFollowups(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
      navigate(ContentRoutes.COMPANIES);
    }
  }

  async function init() {
    try {
      getCustomerId() &&
        getFollowups(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
      getCustomerId() && getViewCustomerDetail(getCustomerId());
    } catch (error) {
      console.log(error);
    }
  }
  const updateResolution = () => {
    setIsWebResolution(isWebResolutionSize());
  };

  useEffect(() => {
    init();
  }, [decryptedParams.id]);

  useEffect(() => {
    window.addEventListener("resize", updateResolution);
  }, []);

  function onEditFollowup() {
    getFollowups(getSearchParams(formData));
  }

  async function onDeleteFollowup() {
    try {
      setPage(1);
      getFollowups(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    } catch (error: any) {
      console.log(error);
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: FollowupListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    params.customer = getCustomerId();
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getFollowups(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getFollowups(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getFollowups(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getFollowups(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterFollowups = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getFollowups(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getFollowups(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getFollowups(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };
  return (
    <div>
      {custLoader && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          content={t("LOADING")}
          center
          backdrop={true}
        />
      )}
      {getCustomerId() > 0 ? (
        <div className="followups">
          <Container className="container">
            <Header className="header mb-2">
              <FlexboxGrid justify="space-between">
                <FlexboxGrid.Item colspan={16}>
                  <h4>
                    <b>
                      {" "}
                      {t("FOLLOWUPS")} {t("FOR")}
                    </b>{" "}
                    {customerInfo?.user?.name} {" ("}{" "}
                    {customerInfo?.user?.mobile} {" )"}
                  </h4>
                  <label>
                    {" "}
                    (<b>{t("NOTE")}</b> : {t("THERE")} {t("ARE").toLowerCase()}{" "}
                    {customerInfo?.pending_follow_ups} {t("OPEN").toLowerCase()}{" "}
                    {t("TICKETS").toLowerCase()} {t("FOR").toLowerCase()}{" "}
                    {t("THIS").toLowerCase()} {t("CUSTOMER").toLowerCase()})
                  </label>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </Header>
            <Content>
              <FollowupFilters
                onSubmit={submitSearch}
                onClear={clearSearch}
                customerId={getCustomerId()}
              />
              <div className="row">
                <div className="col-md-4">
                  <InputGroup>
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity: t("TITLE").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterFollowups(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterFollowups(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>

              <div
                className={
                  total > 0 ? "usr-tbl-border mt-2" : "no-record-table mt-2"
                }
              >
                <Table
                  height={400}
                  fillHeight={true}
                  data={data}
                  loading={loading}
                  onSortColumn={onSortChange}
                  sortColumn={sort_by}
                  bordered={true}
                  cellBordered
                  sortType={sort_order}
                  hover={true}
                >
                  {isWebResolution && (
                    <Column width={30} align="center" fixed>
                      <CustomHeaderCell>
                        <div>
                          <Checkbox
                            inline
                            checked={checked}
                            indeterminate={indeterminate}
                            onChange={handleCheckAll}
                          />
                        </div>
                      </CustomHeaderCell>
                      <CheckCell
                        dataKey="id"
                        checkedKeys={checkedKeys}
                        onChange={handleCheck}
                      />
                    </Column>
                  )}

                  <Column width={200} resizable>
                    <CustomHeaderCell>{t("SUMMARY")}</CustomHeaderCell>
                    <CustomCell dataKey="title">
                      {(rowData: any) => (
                        <span title={`${rowData.title}`}>
                          {displayField(rowData.title)}
                        </span>
                      )}
                    </CustomCell>
                  </Column>

                  <Column flexGrow={1}>
                    <CustomHeaderCell>{t("NOTE")}</CustomHeaderCell>
                    <DescriptionCell
                      rowData={(rowData: any) => rowData}
                      dataIndex={"notes"}
                      introLength={200}
                    />
                  </Column>

                  <Column width={100} sortable>
                    <CustomHeaderCell>{t("STATUS")}</CustomHeaderCell>
                    <CustomCell dataKey="follow_up_status" />
                  </Column>
                  <Column width={130}>
                    <CustomHeaderCell>
                      {t("FOLLOWUP")} {t("FOR")}
                    </CustomHeaderCell>
                    <CustomCell dataKey="follow_up_for" />
                  </Column>
                  <Column width={110} sortable>
                    <CustomHeaderCell>
                      {t("FOLLOW")} {t("UP")} {t("ON")}
                    </CustomHeaderCell>
                    <CustomCell dataKey="follow_up_date">
                      {(rowData: any) => (
                        <span>
                          {displayDayDateField(rowData.follow_up_date)}
                        </span>
                      )}
                    </CustomCell>
                  </Column>
                  <Column width={100} sortable>
                    <CustomHeaderCell>{t("COMMENT")}</CustomHeaderCell>
                    <CustomCell dataKey="comment_count" />
                  </Column>
                  <Column width={80} fixed="right">
                    <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                    <Cell>
                      {(rowData) => (
                        <FollowupSingleAction
                          followupData={rowData}
                          onDeleteFollowup={onDeleteFollowup}
                          onFollowup={onEditFollowup}
                        />
                      )}
                    </Cell>
                  </Column>
                </Table>
              </div>
            </Content>
            <Footer className="footer">
              <Pagination
                prev
                next
                first
                last
                ellipsis
                boundaryLinks
                maxButtons={5}
                size="xs"
                layout={["total", "-", "limit", "|", "pager", "skip"]}
                total={total}
                limitOptions={appConstObj.limit_options}
                limit={rows}
                activePage={page}
                onChangePage={onPageChange}
                onChangeLimit={handleChangeRows}
              />
            </Footer>
            <ToastContainer />
          </Container>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

export default FollowupsForCustomer;
